import { vec3 } from "gl-matrix"
import { clamp } from "@/utils/math"
import { rgbToHsv, rgbToSat, applyTransformToOneDimension, linearToGamma } from "./color-space"
import { capitalize, getToneDiff } from "./utils"
import { Adjustments } from "@/core/types"
import { renderCpu, ColorSpaceCpu, renderHSLAndColorGrading, RenderOptions } from "./renderer-cpu"
import { applyTransform } from "./color-space"
import {
	getWeightedDiff,
	logColorMatch,
	addHue,
	wrapHueDiff,
	gatherRGBValues,
	gatherValues,
	getWeightedMeanRgb,
} from "./utils"

export type GrayColorMean = {
	global: vec3 | null
	shadows: vec3 | null
	midtones: vec3 | null
	highlights: vec3 | null
}

const hsvCurrent = vec3.create()
const hsvTarget = vec3.create()

function getColorDiff(
	rgbCurrent: vec3 | null,
	rgbTarget: vec3 | null,
): {
	satDiff: number
	lumDiff: number
	hueDiff: number
	satTarget: number
	hueTarget: number
} | null {
	if (!rgbCurrent || !rgbTarget) {
		return null
	}
	rgbToHsv(hsvCurrent, rgbCurrent)
	rgbToHsv(hsvTarget, rgbTarget)
	const satCurrent = hsvCurrent[1]
	const satTarget = hsvTarget[1]
	const hueCurrent = hsvCurrent[0]
	const hueTarget = hsvTarget[0]

	const lumCurrent = (Math.max(...rgbCurrent) + Math.min(...rgbCurrent)) / 2
	const lumTarget = (Math.max(...rgbTarget) + Math.min(...rgbTarget)) / 2

	const hueDiff = wrapHueDiff(hueTarget - hueCurrent)
	return {
		satDiff: satTarget - satCurrent,
		lumDiff: lumTarget - lumCurrent,
		hueDiff,
		satTarget,
		hueTarget,
	}
}

export function getGrayPixelIndices(
	image: Float32Array,
	satThreshold: number = 0.1,
): {
	indicesShadow: number[]
	indicesMidtones: number[]
	indicesHighlights: number[]
	indicesGlobal: number[]
} {
	const sat = applyTransformToOneDimension(image, rgbToSat)
	const indicesShadow: number[] = [] // 0 - 0.4 in gamma
	const indicesMidtones: number[] = [] // 0.4 - 0.6 in gamma
	const indicesHighlights: number[] = [] // 0.6 - 0.95 in gamma
	const indicesGlobal: number[] = [] // 0 - 0.95 in gamma
	const threshLow = 0.35
	const threshHigh = 0.65
	for (let i = 0; i < sat.length; i++) {
		if ((sat[i] ?? 1) < satThreshold) {
			const gray =
				((image[3 * i] ?? 0) + (image[3 * i + 1] ?? 0) + (image[3 * i + 2] ?? 0)) / 3
			if (gray < threshLow) {
				indicesShadow.push(i)
			} else if (gray > threshHigh) {
				indicesHighlights.push(i)
			} else {
				indicesMidtones.push(i)
			}
			indicesGlobal.push(i)
		}
	}
	return { indicesShadow, indicesMidtones, indicesHighlights, indicesGlobal }
}

type Region = "global" | "shadows" | "midtones" | "highlights"

export function getColorGrading({
	toneMapInvBefore,
	target,
	adjustments,
	baseRenderOptions,
	pixelWeights,
	refGrayColorMean,
}: {
	toneMapInvBefore: Float32Array
	target: Float32Array
	adjustments: Partial<Adjustments>
	baseRenderOptions: RenderOptions
	pixelWeights: number[]
	refGrayColorMean: GrayColorMean | null
}): Partial<Adjustments> {
	const result: Partial<Adjustments> = {}
	let finalAdjustments: Partial<Adjustments> = adjustments

	const renderOptions = { ...baseRenderOptions, outColorSpace: ColorSpaceCpu.gamma }
	const prophotoImg = renderCpu(toneMapInvBefore, adjustments, {
		...baseRenderOptions,
		inputColorSpace: ColorSpaceCpu.toneMapInversed,
		outColorSpace: ColorSpaceCpu.proPhoto,
	})
	let current = applyTransform(prophotoImg, linearToGamma)

	const indices = getGrayPixelIndices(current)
	const targetRgbs = getGrayMeanRgbs(target, indices, pixelWeights)

	// Check targetRgbs against reference's grayRgbs, and set to null if they are too different
	if (refGrayColorMean) {
		const diffThreshold = 0.02
		if (targetRgbs.global && refGrayColorMean.global) {
			const diffGlobal = getToneDiff(targetRgbs.global, refGrayColorMean.global)
			logColorMatch("[color grading] diffGlobal", {
				diffGlobal,
				targetRgbs: targetRgbs.global,
				refGrayColorMean: refGrayColorMean.global,
			})
			if (diffGlobal > diffThreshold) {
				targetRgbs.global = null
			}
		}
		if (targetRgbs.shadows && refGrayColorMean.shadows) {
			const diffShadows = getToneDiff(targetRgbs.shadows, refGrayColorMean.shadows)
			logColorMatch("[color grading] diffShadows", {
				diffShadows,
				targetRgbs: targetRgbs.shadows,
				refGrayColorMean: refGrayColorMean.shadows,
			})
			if (diffShadows > diffThreshold) {
				targetRgbs.shadows = null
			}
		}
		if (targetRgbs.midtones && refGrayColorMean.midtones) {
			const diffMidtones = getToneDiff(targetRgbs.midtones, refGrayColorMean.midtones)
			logColorMatch("[color grading] diffMidtones", {
				diffMidtones,
				targetRgbs: targetRgbs.midtones,
				refGrayColorMean: refGrayColorMean.midtones,
			})
			if (diffMidtones > diffThreshold) {
				targetRgbs.midtones = null
			}
		}
		if (targetRgbs.highlights && refGrayColorMean.highlights) {
			const diffHighlights = getToneDiff(targetRgbs.highlights, refGrayColorMean.highlights)
			logColorMatch("[color grading] diffHighlights", {
				diffHighlights,
				targetRgbs: targetRgbs.highlights,
				refGrayColorMean: refGrayColorMean.highlights,
			})
			if (diffHighlights > diffThreshold) {
				targetRgbs.highlights = null
			}
		}
	}

	logColorMatch("[color grading] gray indices", indices)
	if (
		!targetRgbs.global &&
		!targetRgbs.shadows &&
		!targetRgbs.midtones &&
		!targetRgbs.highlights
	) {
		logColorMatch("[color grading] no gray pixels before color grading")
		return finalAdjustments
	}

	// Track error for optimization
	const initialError = getWeightedDiff(current, target, pixelWeights)
	let lastError = initialError
	logColorMatch("[color grading] initial error", initialError)

	let useGlobalOnly = false
	let regions: Region[] = ["shadows", "midtones", "highlights"]

	// Iterative optimization
	for (let iterations = 0; iterations < 7; iterations++) {
		// Get current rgbs and calculate differences
		const currentRgbs = getGrayMeanRgbs(current, indices, pixelWeights)
		const colorDiffs = getColorDiffs(currentRgbs, targetRgbs)

		// Determine if we should use only global adjustments
		if (iterations === 0) {
			const { shadows, highlights, global } = colorDiffs
			useGlobalOnly =
				shadows != null &&
				highlights != null &&
				global != null &&
				Math.abs(wrapHueDiff(shadows.hueTarget - highlights.hueTarget)) < 5 / 360 &&
				Math.abs(shadows.satTarget - global.satTarget) < 0.02 &&
				Math.abs(highlights.satTarget - global.satTarget) < 0.02 &&
				Math.abs(shadows.lumDiff - highlights.lumDiff) < 0.04
			regions = useGlobalOnly ? ["global"] : ["shadows", "midtones", "highlights"]
			regions = regions.filter((r) => shouldUpdateRegion(colorDiffs[r]))
			logColorMatch("[color grading] regions", regions, ", colorDiffs", colorDiffs)
		}

		// Apply adjustments for each region
		let updated = false
		for (const region of regions) {
			const stats = colorDiffs[region]
			if (stats) {
				applyRegionalAdjustment(result, stats, iterations, region)
				updated = true
			}
		}

		if (!updated) break

		// Set default grading parameters and render
		result.gradingBalance = 0.0
		result.gradingBlending = 0.5
		current = renderHSLAndColorGrading(prophotoImg, result, renderOptions)

		// Check error and update final adjustments
		const error = getWeightedDiff(current, target, pixelWeights)
		logColorMatch("[color grading] iteration", iterations, "error", error, { ...result })

		// If error is not improving, check if it helps without one of the regions
		// if (error >= lastError && iterations === 0 && regions.length === 3) {
		// 	let newError = error
		// 	let newRegions: Region[] = regions
		// 	let newResult: Partial<Adjustments> = result
		// 	const targetGrayPixels = gatherRGBValues(target, indices.indicesGlobal)
		// 	const grayWeights = gatherValues(pixelWeights, indices.indicesGlobal)
		// 	let minGrayError = 1
		// 	// remove one region at a time and find the best one with the least gray error (not all error)
		// 	for (const region of regions) {
		// 		const tempResult = { ...result }
		// 		delete tempResult[`grading${capitalize(region)}Hue`]
		// 		delete tempResult[`grading${capitalize(region)}Saturation`]
		// 		delete tempResult[`grading${capitalize(region)}Luminance`]

		// 		const newCurrent = renderHSLAndColorGrading(prophotoImg, tempResult, renderOptions)
		// 		const allError = getWeightedDiff(newCurrent, target, pixelWeights)

		// 		const currentGrayPixels = gatherRGBValues(newCurrent, indices.indicesGlobal)
		// 		const newGrayError = getWeightedDiff(
		// 			currentGrayPixels,
		// 			targetGrayPixels,
		// 			grayWeights,
		// 		)
		// 		logColorMatch("[color grading] removed region", { region, allError, newGrayError })

		// 		if (newGrayError < minGrayError && allError < error) {
		// 			minGrayError = newGrayError

		// 			newRegions = regions.filter((r) => r !== region)
		// 			newError = allError
		// 			newResult = tempResult
		// 		}
		// 	}
		// 	regions = newRegions
		// 	result = newResult
		// 	error = newError
		// 	logColorMatch("[color grading] final regions", regions, "new error", error)
		// }

		// Allow the total error to be slightly larger than the initial error,
		// because color grading affects the gray pixels that cannot be edited by HSL or point colors
		const margin = iterations === 0 ? 0.002 : 0.0
		if (error >= lastError + margin) break

		finalAdjustments = { ...finalAdjustments, ...result }
		lastError = error
	}

	// If the final color grading saturation is small for each region, return the initial adjustments for that region
	if (
		finalAdjustments.gradingGlobalSaturation &&
		finalAdjustments.gradingGlobalSaturation <= 0.02
	) {
		finalAdjustments.gradingGlobalSaturation = 0.0
		finalAdjustments.gradingGlobalHue = 0.0
		finalAdjustments.gradingGlobalLuminance = 0.0
	}
	if (
		finalAdjustments.gradingShadowsSaturation &&
		finalAdjustments.gradingShadowsSaturation <= 0.02
	) {
		finalAdjustments.gradingShadowsSaturation = 0.0
		finalAdjustments.gradingShadowsHue = 0.0
		finalAdjustments.gradingShadowsLuminance = 0.0
	}
	if (
		finalAdjustments.gradingMidtonesSaturation &&
		finalAdjustments.gradingMidtonesSaturation <= 0.02
	) {
		finalAdjustments.gradingMidtonesSaturation = 0.0
		finalAdjustments.gradingMidtonesHue = 0.0
		finalAdjustments.gradingMidtonesLuminance = 0.0
	}
	if (
		finalAdjustments.gradingHighlightsSaturation &&
		finalAdjustments.gradingHighlightsSaturation <= 0.02
	) {
		finalAdjustments.gradingHighlightsSaturation = 0.0
		finalAdjustments.gradingHighlightsHue = 0.0
		finalAdjustments.gradingHighlightsLuminance = 0.0
	}

	if (lastError > initialError + 0.0012) {
		return adjustments
	}
	return finalAdjustments
}

// Helper functions
export function getGrayMeanRgbs(
	target: Float32Array,
	indices: ReturnType<typeof getGrayPixelIndices>,
	pixelWeights: number[] | Float32Array,
): GrayColorMean {
	return {
		global: getWeightedMeanRgb(
			gatherRGBValues(target, indices.indicesGlobal),
			gatherValues(pixelWeights, indices.indicesGlobal),
		),
		shadows: getWeightedMeanRgb(
			gatherRGBValues(target, indices.indicesShadow),
			gatherValues(pixelWeights, indices.indicesShadow),
		),
		midtones: getWeightedMeanRgb(
			gatherRGBValues(target, indices.indicesMidtones),
			gatherValues(pixelWeights, indices.indicesMidtones),
		),
		highlights: getWeightedMeanRgb(
			gatherRGBValues(target, indices.indicesHighlights),
			gatherValues(pixelWeights, indices.indicesHighlights),
		),
	}
}

function getColorDiffs(
	current: Record<Region, vec3 | null>,
	target: Record<Region, vec3 | null>,
): Record<Region, ReturnType<typeof getColorDiff> | null> {
	return {
		global: getColorDiff(current.global, target.global),
		shadows: getColorDiff(current.shadows, target.shadows),
		midtones: getColorDiff(current.midtones, target.midtones),
		highlights: getColorDiff(current.highlights, target.highlights),
	}
}

function shouldUpdateRegion(stats: NonNullable<ReturnType<typeof getColorDiff>> | null): boolean {
	return (
		stats != null &&
		(Math.abs(stats.hueDiff * 360) > 2 || (stats.satDiff > 0.01 && stats.satTarget > 0.03))
	)
}

function applyRegionalAdjustment(
	result: Partial<Adjustments>,
	stats: NonNullable<ReturnType<typeof getColorDiff>>,
	iterations: number,
	region: Region,
): void {
	const prefix = region === "global" ? "gradingGlobal" : `grading${capitalize(region)}`

	// Apply hue adjustment
	const hueDelta = iterations > 0 ? clamp(stats.hueDiff, -0.04, 0.04) : stats.hueTarget
	result[`${prefix}Hue`] = addHue(result[`${prefix}Hue`] ?? 0, hueDelta)

	// Apply saturation adjustment
	let satScale = 1.0 // shadows: 2, midtones: 1.0, highlights: 1.5
	if (region === "shadows") {
		satScale = 0.75
	} else if (region === "highlights") {
		satScale = 0.9
	}
	if (iterations === 0) {
		satScale *= 0.8
	}
	const satDelta =
		iterations > 0
			? clamp(stats.satDiff * satScale, -0.05, 0.05)
			: clamp(stats.satTarget * satScale, 0.02, 0.1)

	result[`${prefix}Saturation`] = clamp(
		(result[`${prefix}Saturation`] ?? 0) + satDelta,
		0.02,
		0.4,
	)

	// Apply luminance adjustment
	result[`${prefix}Luminance`] = clamp(
		(result[`${prefix}Luminance`] ?? 0) + stats.lumDiff,
		-0.25,
		0.25,
	)
}
